import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import WcIcon from "@mui/icons-material/Wc";
import CakeOutlinedIcon from "@mui/icons-material/CakeOutlined";
import TranslateIcon from "@mui/icons-material/Translate";
import PublicIcon from "@mui/icons-material/Public";
import DevicesOutlinedIcon from "@mui/icons-material/DevicesOutlined";
import InterestsOutlinedIcon from "@mui/icons-material/InterestsOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import BlockIcon from "@mui/icons-material/Block";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  AssociatedCampaignTarget,
  useCreateAssociatedCampaignTargetMutation,
  useGetAssociatedCampaignsQuery,
  useUpdateAssociatedCampaignTargetMutation,
} from "../../store/apis/briefingApi";
import { useGetCompanyAudiencesQuery } from "../../store/apis/companyApi";
import { uploadBriefingTargetImage } from "../../firebase/firebaseStorage";
import { setToastMessage } from "../../store/slices/app/appSlice";
import { getLanguageLabel } from "../../utils/languageUtils";
import { LanguageSelect } from "../common/LanguageSelect";
import SectionCard from "./inline/SectionCard";
import FieldGroup from "./inline/FieldGroup";
import InlineField from "./inline/InlineField";
import { AgeRangeInput, AgeRangeTrack } from "./inline/AgeRange";
import { InlineSelect, InlineText, SectionEmpty } from "./inline/inputs";
import {
  bindField,
  FieldErrors,
  useInlineSection,
} from "./inline/useInlineSection";
import { extractApiErrorMessage } from "./inline/apiErrors";

interface TargetDraft {
  sex: string;
  minimumAge: string;
  maximumAge: string;
  /** Comma-separated language codes, as stored. */
  language: string;
  geography: string;
  technology: string;
  interests: string;
  dataAudience: string;
  exclusions: string;
  notes: string;
  imageUrl: string;
}
type TargetKey = keyof TargetDraft;

const EMPTY_TARGET: TargetDraft = {
  sex: "BOTH",
  minimumAge: "",
  maximumAge: "",
  language: "ANY",
  geography: "",
  technology: "",
  interests: "",
  dataAudience: "",
  exclusions: "",
  notes: "",
  imageUrl: "",
};

type TargetLike = Partial<
  Record<TargetKey, string | number | null | undefined>
>;

const toDraft = (source: TargetLike): TargetDraft => {
  const draft = { ...EMPTY_TARGET };
  (Object.keys(EMPTY_TARGET) as TargetKey[]).forEach((key) => {
    const value = source[key];
    draft[key] = value === null || value === undefined ? "" : String(value);
  });
  if (!draft.sex) draft.sex = "BOTH";
  return draft;
};

interface AssociatedCampaignTargetSectionProps {
  briefingId: string;
  associatedId: string;
  companyId?: string;
  target: AssociatedCampaignTarget | null;
  canEdit: boolean;
  onDelete?: () => void;
  /** Reloads the campaign; resolves once fresh data is in. */
  onSaved: () => Promise<unknown>;
}

const AssociatedCampaignTargetSection: React.FC<
  AssociatedCampaignTargetSectionProps
> = ({
  briefingId,
  associatedId,
  companyId,
  target,
  canEdit,
  onDelete,
  onSaved,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [creating, setCreating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [createTarget] = useCreateAssociatedCampaignTargetMutation();
  const [updateTarget] = useUpdateAssociatedCampaignTargetMutation();

  const base = useMemo<TargetDraft>(
    () =>
      target && !creating
        ? toDraft(target as unknown as TargetLike)
        : EMPTY_TARGET,
    [target, creating]
  );

  const validate = (draft: TargetDraft): FieldErrors<TargetDraft> => {
    const errors: FieldErrors<TargetDraft> = {};
    const required = t("validation.required", "This field is required");
    const invalid = t("validation.invalidNumber", "Enter a valid number");
    (["minimumAge", "maximumAge"] as TargetKey[]).forEach((key) => {
      if (!draft[key].trim()) errors[key] = required;
      else if (Number.isNaN(Number(draft[key]))) errors[key] = invalid;
    });
    if (!draft.geography.trim()) errors.geography = required;
    if (!draft.interests.trim()) errors.interests = required;
    const min = Number(draft.minimumAge);
    const max = Number(draft.maximumAge);
    if (!errors.minimumAge && !errors.maximumAge && min > max) {
      const message = t(
        "briefingsView.targetForm.minGreaterThanMax",
        "Minimum age cannot be greater than maximum age"
      );
      errors.minimumAge = message;
      errors.maximumAge = message;
    }
    return errors;
  };

  const handleSave = async (draft: TargetDraft) => {
    const errors = validate(draft);
    if (Object.keys(errors).length > 0) return errors;

    const isCreate = creating || !target;
    const body = {
      sex: draft.sex as AssociatedCampaignTarget["sex"],
      minimumAge: Number(draft.minimumAge),
      maximumAge: Number(draft.maximumAge),
      language: draft.language || undefined,
      geography: draft.geography.trim(),
      technology: draft.technology.trim() || undefined,
      interests: draft.interests.trim(),
      dataAudience: draft.dataAudience.trim() || undefined,
      exclusions: draft.exclusions.trim() || undefined,
      notes: draft.notes.trim() || undefined,
      imageUrl: draft.imageUrl.trim() || undefined,
    };
    try {
      const params = { briefingId, associatedId };
      if (isCreate) await createTarget({ params, body }).unwrap();
      else await updateTarget({ params, body }).unwrap();
      await onSaved();
      setCreating(false);
      dispatch(
        setToastMessage({
          message: isCreate
            ? t(
                "briefingsView.targetCreateSuccess",
                "Targeting details created successfully"
              )
            : t(
                "briefingsView.targetUpdateSuccess",
                "Targeting details updated successfully"
              ),
          severity: "success",
        })
      );
      return true;
    } catch (error) {
      dispatch(
        setToastMessage({
          message:
            extractApiErrorMessage(error) ??
            (isCreate
              ? t(
                  "briefingsView.targetCreateError",
                  "Failed to create targeting details"
                )
              : t(
                  "briefingsView.targetUpdateError",
                  "Failed to update targeting details"
                )),
          severity: "error",
        })
      );
      return false;
    }
  };

  const section = useInlineSection<TargetDraft>({
    id: "target",
    base,
    onSave: handleSave,
    forceEdit: creating,
    readOnly: !canEdit || (!target && !creating),
  });
  const { draft } = section;

  // --- "fill from" sources, only needed while editing ---
  const anyEditing =
    creating ||
    (Object.keys(EMPTY_TARGET) as TargetKey[]).some((key) =>
      section.isEditing(key)
    );
  const { data: associatedCampaigns } = useGetAssociatedCampaignsQuery(
    { id: briefingId },
    { skip: !anyEditing }
  );
  const { data: audiencesData } = useGetCompanyAudiencesQuery(
    companyId as string,
    { skip: !anyEditing || !companyId }
  );
  const audiences = audiencesData?.data ?? [];
  const copyableCampaigns = (associatedCampaigns?.data ?? []).filter(
    (c) => c.id !== associatedId && c.target
  );

  const fillFrom = (source: TargetLike, keepMissing: boolean) => {
    const values = toDraft(source);
    if (keepMissing) {
      (Object.keys(values) as TargetKey[]).forEach((key) => {
        if (!values[key]) values[key] = draft[key];
      });
    }
    section.setFields(values);
  };

  const handleCopyFromCampaign = (campaignId: string) => {
    const campaign = copyableCampaigns.find((c) => c.id === campaignId);
    if (!campaign?.target) return;
    fillFrom(campaign.target as unknown as TargetLike, false);
    dispatch(
      setToastMessage({
        message: t("briefingsView.targetCopied", "Target copied successfully"),
        severity: "success",
      })
    );
  };

  const handleCopyFromAudience = (audienceId: string) => {
    const audience = audiences.find((a) => a.id === audienceId);
    if (!audience) return;
    fillFrom(audience as unknown as TargetLike, true);
    dispatch(
      setToastMessage({
        message: t(
          "briefingsView.audienceImported",
          "Audience applied successfully. Please review the populated fields."
        ),
        severity: "success",
      })
    );
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setIsUploading(true);
      const url = await uploadBriefingTargetImage(file, associatedId);
      section.setField("imageUrl", url);
    } catch (error) {
      console.error("Failed to upload image", error);
      dispatch(
        setToastMessage({
          message: t(
            "briefingsView.targetImageUploadError",
            "Failed to upload image"
          ),
          severity: "error",
        })
      );
    } finally {
      setIsUploading(false);
    }
  };

  const sexLabel = (sex: string) =>
    sex === "MAN"
      ? t("briefingsView.target.sexMan", "Men")
      : sex === "WOMAN"
        ? t("briefingsView.target.sexWoman", "Women")
        : t("briefingsView.target.sexBoth", "Both");

  const textField = (
    key: TargetKey,
    label: string,
    icon: React.ReactElement,
    span: number | "full" = 1,
    // Set → textarea that starts at this height and grows with the text
    minRows?: number
  ) => (
    <InlineField
      key={key}
      span={span}
      label={label}
      icon={icon}
      empty={!draft[key]}
      value={draft[key]}
      copyValue={draft[key]}
      input={
        <InlineText
          value={draft[key]}
          onChange={(event) => section.setField(key, event.target.value)}
          error={Boolean(section.errors[key])}
          autoFocus={section.isSoloEditing(key)}
          multiline={minRows !== undefined}
          minRows={minRows}
        />
      }
      {...bindField(section, key, canEdit)}
    />
  );

  // Edad mínima y máxima en un solo campo: un slider con el tramo de edad. Se
  // edita, revierte y valida como los dos campos que guarda.
  const ageKeys = ["minimumAge", "maximumAge"] as const;
  const ageStates = ageKeys.map((key) => section.fieldState(key));
  const ageEditing = ageKeys.some((key) => section.isEditing(key));
  const ageSolo = ageKeys.some((key) => section.isSoloEditing(key));
  const revertAge = () => ageKeys.forEach((key) => section.cancelField(key));
  const minAgeLabel = t("briefingsView.target.minimumAge", "Minimum age");
  const maxAgeLabel = t("briefingsView.target.maximumAge", "Maximum age");
  const ageField = (
    <InlineField
      span={2}
      label={t("briefingsView.target.ageRange", "Age")}
      icon={<CakeOutlinedIcon />}
      empty={!draft.minimumAge && !draft.maximumAge}
      value={
        <AgeRangeTrack min={draft.minimumAge} max={draft.maximumAge} />
      }
      copyValue={
        draft.minimumAge && draft.maximumAge
          ? t("briefingsView.target.ageRangeValue", {
              defaultValue: "{{min}} to {{max}} years",
              min: draft.minimumAge,
              max: draft.maximumAge,
            })
          : ""
      }
      input={
        <AgeRangeInput
          min={draft.minimumAge}
          max={draft.maximumAge}
          minLabel={minAgeLabel}
          maxLabel={maxAgeLabel}
          minError={Boolean(section.errors.minimumAge)}
          maxError={Boolean(section.errors.maximumAge)}
          autoFocus={ageSolo}
          onChange={(minimumAge, maximumAge) =>
            section.setFields({ minimumAge, maximumAge })
          }
        />
      }
      {...bindField(section, "minimumAge", canEdit, revertAge)}
      state={
        ageStates.includes("error")
          ? "error"
          : ageStates.includes("dirty")
            ? "dirty"
            : ageStates.includes("saved")
              ? "saved"
              : "idle"
      }
      editing={canEdit && ageEditing}
      solo={ageSolo}
      error={section.errors.minimumAge || section.errors.maximumAge}
    />
  );

  const hasTarget = Boolean(target) || creating;
  const languageCodes = draft.language
    ? draft.language.split(",").map((code) => code.trim())
    : [];

  const fillToolbar = anyEditing &&
    (audiences.length > 0 || copyableCampaigns.length > 0) && (
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ minWidth: { sm: 420 } }}
      >
        {audiences.length > 0 && (
          <InlineSelect
            value=""
            label={t(
              "briefingsView.copyTargetFromAudience",
              "Populate from Company Audience"
            )}
            options={audiences.map((a) => ({ value: a.id, label: a.name }))}
            onChange={handleCopyFromAudience}
          />
        )}
        {copyableCampaigns.length > 0 && (
          <InlineSelect
            value=""
            label={t("briefingsView.copyTargetFrom", "Copy from another line")}
            options={copyableCampaigns.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            onChange={handleCopyFromCampaign}
          />
        )}
      </Stack>
    );

  return (
    <SectionCard
      icon={<GroupsOutlinedIcon />}
      title={t("briefingsView.targetSectionTitle", "Audience targeting")}
      subtitle={t(
        "briefingsView.targetSectionSubtitle",
        "A quién se dirige la línea"
      )}
      dirtyCount={section.dirtyCount}
      saving={section.isSaving}
      saveLabel={
        creating
          ? t("briefingsView.targetCreateCta", "Create target")
          : undefined
      }
      onSave={() => void section.save()}
      onDiscard={() => {
        section.discard();
        setCreating(false);
      }}
      createdAt={creating ? null : target?.createdAt}
      updatedAt={creating ? null : target?.updatedAt}
      actions={
        target && onDelete ? (
          <Tooltip title={t("briefingsView.targetDeleteCta", "Delete target")}>
            <IconButton
              size="small"
              onClick={onDelete}
              sx={{
                color: "text.secondary",
                "&:hover": { color: "error.main" },
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : undefined
      }
    >
      {!hasTarget ? (
        <SectionEmpty
          message={t(
            "briefingsView.targetNotFound",
            "This associated campaign does not have targeting details yet."
          )}
          actionLabel={
            canEdit
              ? t("briefingsView.targetCreateCta", "Create target")
              : undefined
          }
          onAction={() => setCreating(true)}
        />
      ) : (
        <Stack spacing={3}>
          <FieldGroup
            title={t("briefingsView.groups.demographics", "Demografía")}
            columns={4}
            action={fillToolbar || undefined}
          >
            <InlineField
              label={t("briefingsView.target.sex", "Sex")}
              icon={<WcIcon />}
              value={sexLabel(draft.sex)}
              copyValue={sexLabel(draft.sex)}
              input={
                <InlineSelect
                  value={draft.sex}
                  options={["MAN", "WOMAN", "BOTH"].map((sex) => ({
                    value: sex,
                    label: sexLabel(sex),
                  }))}
                  onChange={(value) => section.setField("sex", value)}
                />
              }
              {...bindField(section, "sex", canEdit)}
            />
            {ageField}
            <InlineField
              label={t("briefingsView.target.language", "Language")}
              icon={<TranslateIcon />}
              empty={!draft.language}
              value={getLanguageLabel(draft.language, t)}
              copyValue={getLanguageLabel(draft.language, t)}
              input={
                <LanguageSelect
                  multiple
                  label=""
                  value={languageCodes}
                  onChange={(value) =>
                    section.setField("language", (value as string[]).join(","))
                  }
                />
              }
              {...bindField(section, "language", canEdit)}
            />
          </FieldGroup>

          <FieldGroup
            title={t("briefingsView.groups.segmentation", "Segmentación")}
            columns={3}
          >
            {textField(
              "geography",
              t("briefingsView.target.geography", "Geography"),
              <PublicIcon />
            )}
            {textField(
              "interests",
              t("briefingsView.target.interests", "Interests"),
              <InterestsOutlinedIcon />,
              1,
              1
            )}
            {textField(
              "technology",
              t("briefingsView.target.technology", "Technology"),
              <DevicesOutlinedIcon />,
              1,
              1
            )}
            {textField(
              "dataAudience",
              t("briefingsView.target.dataAudience", "Data audience"),
              <StorageOutlinedIcon />
            )}
            {textField(
              "exclusions",
              t("briefingsView.target.exclusions", "Exclusions"),
              <BlockIcon />,
              2
            )}
          </FieldGroup>

          <FieldGroup
            title={t("briefingsView.groups.notesAndFiles", "Notas y adjuntos")}
            columns={3}
          >
            {textField(
              "notes",
              t("briefingsView.target.notes", "Notes"),
              <NotesOutlinedIcon />,
              2,
              3
            )}
            <InlineField
              label={t("briefingsView.target.image", "Attachment image")}
              icon={<ImageOutlinedIcon />}
              empty={!draft.imageUrl}
              copyValue={draft.imageUrl}
              value={
                <Box
                  component="img"
                  src={draft.imageUrl}
                  alt=""
                  sx={{
                    maxHeight: 160,
                    maxWidth: "100%",
                    borderRadius: 1.5,
                    display: "block",
                    objectFit: "cover",
                  }}
                />
              }
              input={
                <Stack direction="row" spacing={1} alignItems="center">
                  {draft.imageUrl && (
                    <Box
                      component="img"
                      src={draft.imageUrl}
                      alt=""
                      sx={{ height: 64, borderRadius: 1, objectFit: "cover" }}
                    />
                  )}
                  <Button
                    component="label"
                    size="small"
                    variant="outlined"
                    disabled={isUploading}
                    startIcon={
                      isUploading ? (
                        <CircularProgress size={14} />
                      ) : (
                        <CloudUploadOutlinedIcon />
                      )
                    }
                  >
                    {isUploading
                      ? t("common.uploading", "Uploading...")
                      : t("briefingsView.target.uploadImage", "Upload image")}
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={handleImageUpload}
                    />
                  </Button>
                  {draft.imageUrl && (
                    <Tooltip title={t("common.delete", "Delete")}>
                      <IconButton
                        size="small"
                        onClick={() => section.setField("imageUrl", "")}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Stack>
              }
              {...bindField(section, "imageUrl", canEdit)}
            />
          </FieldGroup>
        </Stack>
      )}
    </SectionCard>
  );
};

export default AssociatedCampaignTargetSection;
